/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import snakeCase from 'lodash/snakeCase';
import times from 'lodash/times';

import { Grid, Hidden, Link, Typography, Typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import ArticleHubCardPlaceholder from 'bundles/shared-articles/components/hub-page/ArticleHubCardPlaceholder';
import ArticleHubCardWithEstimatedReadTime from 'bundles/unified-seo-content-common/components/hub-page/ArticleHubCardWithEstimatedReadTime';
import {
  NUMBER_OF_ITEMS_PER_LINE_DESKTOP,
  NUMBER_OF_ITEMS_PER_LINE_MOBILE,
} from 'bundles/unified-seo-content-common/constants';
import type { SeoContentCard } from 'bundles/unified-seo-content-common/types';
import { scrollPageToTop } from 'bundles/unified-seo-content-common/utils';

import _t from 'i18n!nls/articles';

type InputProps = {
  collectionHeaderText?: string;
  viewAllCTA?: string;
  isLoading?: boolean;
  contentItems?: Array<SeoContentCard | null>;
  imageLazyLoad: boolean;
  isFeaturedContentCollection?: boolean;
};

export type Props = InputProps;

const styles = {
  collectionHeader: css`
    margin-bottom: 24px;
  `,
  viewAllLink: css`
    text-decoration: none !important;

    span {
      color: var(--cds-color-grey-950);
    }
  `,
};

const ContentItem = ({
  item,
  imageLazyLoad,
  isFeaturedContentCollection,
}: {
  item: SeoContentCard;
  imageLazyLoad: boolean;
  isFeaturedContentCollection?: boolean;
}) => {
  if (!item?.slug) {
    return null;
  }
  return (
    <ArticleHubCardWithEstimatedReadTime
      title={item.title || _t('Coursera Article')}
      subtitle={item.intro || _t('This is a Coursera Article')}
      slug={item.slug}
      metadata={{
        publishedDate: item.sys.publishedAt,
      }}
      image={{
        url: item.featuredImage?.url || '',
        description: item.featuredImage?.description || _t('Article Image'),
      }}
      imageLazyLoad={imageLazyLoad}
      localizedCountryCode={item?.countryCode ?? undefined}
      isFeatured={isFeaturedContentCollection}
    />
  );
};

export const CategoryCollection = ({
  isLoading,
  collectionHeaderText,
  viewAllCTA,
  contentItems,
  imageLazyLoad,
  isFeaturedContentCollection,
}: Props) => {
  if (!isLoading && !contentItems?.length) {
    return null;
  }

  const showCollectionHeader = collectionHeaderText && viewAllCTA;

  return (
    <div>
      {showCollectionHeader && (
        <Grid css={styles.collectionHeader} container>
          <Grid item xs={9}>
            <Typography component="h3" variant="h2semibold">
              {collectionHeaderText}
            </Typography>
          </Grid>
          <Grid item container xs={3} justifyContent="flex-end">
            <Link
              css={styles.viewAllLink}
              component={TrackedLink2}
              href={viewAllCTA}
              trackingName={`${snakeCase(collectionHeaderText)}_view_all`}
              icon={<ArrowNextIcon size="small" />}
              iconPosition="after"
              variant="quiet"
              onClick={scrollPageToTop}
            >
              <Typography2 variant="subtitleMedium" component="span">
                {_t('View all')}
              </Typography2>
            </Link>
          </Grid>
        </Grid>
      )}
      <Grid container wrap="nowrap" spacing={16}>
        {isLoading
          ? times(NUMBER_OF_ITEMS_PER_LINE_DESKTOP, (index) => (
              <Grid
                component={Hidden}
                smDown={index >= NUMBER_OF_ITEMS_PER_LINE_MOBILE}
                item
                xs
                key={`hub-card-placeholder-${index}`}
              >
                <ArticleHubCardPlaceholder />
              </Grid>
            ))
          : contentItems?.map(
              (item, index) =>
                item && (
                  <Grid component={Hidden} smDown={index >= NUMBER_OF_ITEMS_PER_LINE_MOBILE} item xs key={item.slug}>
                    <ContentItem
                      item={item}
                      imageLazyLoad={imageLazyLoad}
                      isFeaturedContentCollection={isFeaturedContentCollection}
                    />
                  </Grid>
                )
            )}
      </Grid>
    </div>
  );
};

export default CategoryCollection;
